import {PageOrderPosition} from '~shared/models';

import {DropPosition, NavigationEntityState} from '~ui/store/slices/navigation-tree/types';

import {isString} from '~ui/utils';

import {getNewSlug} from './get-new-slug';
import {getPositionData} from './get-position-data';
import {isInnerDrop} from './is-inner-drop';

import {addChild, removeChildAtIndex} from './children';

export interface MovePageRevertableOperation {
    // real slug of the page that is used to access page
    // from the store and request page data
    slug: string;

    // the operation description that will be sent to the backend
    operation: {
        source: string;
        target: string;
        nextToSlug?: string;
        position?: PageOrderPosition;
    };

    // new position of the page after the operation will be applied
    newPosition: {
        parentSlug: string;
        index: number;
    };

    // position of the page before the operation was applied
    prevPosition: {
        parentSlug: string;
        index: number;
    };
}

export function createOperation(
    entities: Record<string, NavigationEntityState>,
    moveItemSlug: string,
    targetItemSlug: string,
    dropPosition: DropPosition,
    moveItemName?: string,
) {
    const moveItem = entities[moveItemSlug];
    const targetItem = entities[targetItemSlug];

    if (!moveItem || !targetItem) {
        return;
    }

    const moveItemVirtualSlug = moveItem.virtualSlug || moveItemSlug;

    const moveItemParent = isString(moveItem.parent) ? entities[moveItem.parent] : undefined;
    const targetItemParent = isString(targetItem.parent) ? entities[targetItem.parent] : undefined;

    const innerDrop = isInnerDrop(targetItem, dropPosition);

    if (!innerDrop && !targetItemParent) {
        return;
    }

    if (!moveItemParent) {
        return;
    }

    const moveItemIndex = moveItemParent.children.indexOf(moveItemSlug);
    // target item can have no parent only in `inner` drop.
    // we need `targetItemIndex` only for the `bottom` or `top` drop, there for
    // we could safely cast it to the 0.
    const targetItemIndex = targetItemParent?.children.indexOf(targetItemSlug) || 0;

    if (moveItemIndex === -1 || targetItemIndex === -1) {
        return;
    }

    const positionData = getPositionData(entities, targetItem, dropPosition);

    const newPositionItemParent = innerDrop
        ? targetItem
        : // targetItemParent can be undefined only if the `innerDrop` is `true`
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          targetItemParent!;
    const targetItemParentSlug = newPositionItemParent.slug;
    const targetItemParentVirtualSlug =
        newPositionItemParent.virtualSlug || newPositionItemParent.slug;

    const getNewPositionIndex = (innerDrop: boolean, isReorder: boolean) => {
        if (innerDrop) {
            return 0;
        }

        // the new index of the move item before
        // it was deleted from it's current parent
        const beforeRemoveNewPositionIndex =
            dropPosition === 'bottom' ? targetItemIndex + 1 : targetItemIndex;

        // if we have a move between clusters or
        // moveItem position is below the target item
        // the new index will be the same
        if (!isReorder || moveItemIndex > targetItemIndex) {
            return beforeRemoveNewPositionIndex;
        }

        // if the move item is in the same cluster as target item and
        // move item positioned before target item, we must subtract 1 from
        // newPositionIndex, because all array elements will be moved
        // after we splice the move item
        return beforeRemoveNewPositionIndex - 1;
    };

    const isReorder = moveItemParent.slug === targetItemParentSlug;
    const newPositionIndex = getNewPositionIndex(innerDrop, isReorder);

    const operation: MovePageRevertableOperation = {
        slug: moveItemSlug,

        operation: {
            source: moveItemVirtualSlug,
            target: getNewSlug(moveItemSlug, targetItemParentVirtualSlug, moveItemName),
            ...positionData,
        },

        newPosition: {
            parentSlug: targetItemParentSlug,
            index: newPositionIndex,
        },

        prevPosition: {
            index: moveItemIndex,
            parentSlug: moveItemParent.slug,
        },
    };

    return operation;
}

export function applyOperation(
    entities: Record<string, NavigationEntityState>,
    operation: MovePageRevertableOperation,
) {
    const {
        slug,
        prevPosition: {parentSlug: currentParentSlug, index: currentParentIndex},
        newPosition: {parentSlug: newParentSlug, index: newParentIndex},
        operation: {target: newVirtualSlug},
    } = operation;

    const movingPage = entities[slug];
    const newParent = entities[newParentSlug];
    const currentParent = entities[currentParentSlug];

    if (!movingPage || !newParent || !currentParent) {
        return;
    }

    removeChildAtIndex(currentParent, currentParentIndex);
    addChild(newParent, movingPage, newParentIndex);

    movingPage.virtualSlug = newVirtualSlug;
    updateChildrenVirtualSlugs(entities, movingPage);
}

export function revertOperation(
    entities: Record<string, NavigationEntityState>,
    operation: MovePageRevertableOperation,
) {
    const {
        slug,
        prevPosition: {parentSlug: prevParentSlug, index: prevParentIndex},
        newPosition: {parentSlug: currentParentSlug, index: currentParentIndex},
        operation: {source: prevVirtualSlug},
    } = operation;

    const movingPage = entities[slug];
    const prevParent = entities[prevParentSlug];
    const currentParent = entities[currentParentSlug];

    if (!movingPage || !prevParent || !currentParent) {
        return;
    }

    removeChildAtIndex(currentParent, currentParentIndex);
    addChild(prevParent, movingPage, prevParentIndex);

    // set a virtual slug to `undefined` if it's the same as a page slug
    movingPage.virtualSlug = prevVirtualSlug === slug ? undefined : prevVirtualSlug;
    updateChildrenVirtualSlugs(entities, movingPage);
}

function updateChildrenVirtualSlugs(
    entities: Record<string, NavigationEntityState>,
    moveItem: NavigationEntityState,
) {
    // if the parent virtual slug is `undefined`(i.e. it's in it's original place),
    // we still can not safely remove children virtual slugs, since we may have
    // "foreign" children that are not saved yet
    const moveItemVirtualSlug = moveItem.virtualSlug || moveItem.slug;

    moveItem.children.forEach((childSlug) => {
        const child = entities[childSlug];

        if (!child) {
            return;
        }

        const newChildVirtualSlug = getNewSlug(child.slug, moveItemVirtualSlug);

        // set a virtual slug to `undefined` if it's the same as a page slug
        child.virtualSlug = newChildVirtualSlug === child.slug ? undefined : newChildVirtualSlug;

        updateChildrenVirtualSlugs(entities, child);
    });
}
